import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb'
import HomeIcon from '@mui/icons-material/Home'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import WbSunnyIcon from '@mui/icons-material/WbSunny'
import { useTranslation } from 'next-i18next'
import React, { useState } from 'react'
import { INDOOR_OUTDOOR_MAP } from 'src/constants'
import { useListingVariant } from 'src/lib/listingVariants'
import {
  IConfiguration,
  IConfigurationInput,
  ILocation,
  IndoorOutdoor
} from '../../types/graphql'
import {
  findClosestSize,
  MAX_SIZE_GUIDE_AREA_SQM,
  SIZE_GUIDE_CATEGORIES,
  SIZE_IMAGE_MAP
} from '../Modals/StorageSizeModal/constants'
import StorageSizeModal from '../Modals/StorageSizeModal/StorageSizeModal'

// Escape `< > & "` so an i18n value containing an angle bracket can never
// break out of the dangerouslySetInnerHTML payload below. Defensive — locale
// JSON in this repo is author-controlled, but cheap insurance.
const escapeForHtml = (s: string) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

interface IProps {
  location: ILocation
  activeConfig: IConfigurationInput | IConfiguration
  isBrokerMode: boolean
}

function getSizeInfo(area: number, isVehicle: boolean) {
  const { categoryId, sizeId } = findClosestSize(area, isVehicle)
  const category = SIZE_GUIDE_CATEGORIES.find((c) => c.id === categoryId)
  const size = category?.sizes.find((s) => s.id === sizeId)
  const image = size ? SIZE_IMAGE_MAP[size.i18nKey] : null

  const labelMap: Record<string, string> = {
    small: 'Chico',
    medium: 'Mediano',
    large: 'Grande',
    vehicle: 'Vehículo',
    fleet: 'Flotilla'
  }

  const label = labelMap[categoryId] ?? labelMap.medium
  return { categoryId, sizeId, image, label }
}

const SizeSection = ({ location, activeConfig, isBrokerMode }: IProps) => {
  const { privateSpace, indoorOrOutdoor } = location ?? {}
  const config = activeConfig
  const { size, canStoreVehicle } = config ?? {}
  const [guideOpen, setGuideOpen] = useState(false)
  const width = size?.width || 1
  const length = size?.length || 1
  const area = Number((width * length).toFixed(2))
  const heightMeters = typeof size?.height === 'number' ? size.height : null

  const { categoryId, sizeId, image, label } = getSizeInfo(
    area,
    !!canStoreVehicle
  )

  const indoorOutdoorData = INDOOR_OUTDOOR_MAP[indoorOrOutdoor as IndoorOutdoor]
  const isIndoor = indoorOrOutdoor === IndoorOutdoor.Indoor

  const variant = useListingVariant()
  const { t } = useTranslation('details')
  const sizeFraming = variant.sections.size.framing

  // Unit 15 — section hidden entirely when the variant says so. (None of
  // today's variants set `show: false`, but the resolver could.)
  if (!variant.sections.size.show) return null

  // Legacy behavior preserved: Monthly + Broker pillar (the prop) hides
  // the section. The Traditional variant continues to render b2c framing
  // by default; Broker / Fleet variants opt into the b2b minimal render
  // via `framing: 'b2b'`. The legacy `isBrokerMode` prop comes from
  // `displayMode.isBrokerMode` (parking + broker pillar), which is
  // unrelated to the variantKey — keep the early return for backward
  // compatibility on the parking pillar.
  if (isBrokerMode) return null

  // Storage size guide tops out at 27 m² (3×9 Grande). Anything larger sits
  // outside its frame of reference — the closest-match lookup would still
  // pick "Grande" and render a misleading thumbnail. Same applies on the
  // parking side once a listing exceeds normal-vehicle sizing. Hide the
  // section above the guide's representational ceiling regardless of
  // variant — covers oversized Monthly / Traditional listings that today
  // render a "fits 30 boxes" framing on a 45 m² space.
  if (area > MAX_SIZE_GUIDE_AREA_SQM) return null

  // Unit 15 — b2b framing: m² + dimensions + clear height + access line.
  // No "fits 30 boxes" cute copy, no size-guide CTA, no reference image.
  if (sizeFraming === 'b2b') {
    return (
      <section className="border-t border-ink-100/15 py-12 md:py-24">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-12">
          <div className="md:col-span-5">
            <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
              {t('SIZE.B2B.EYEBROW')}
            </p>
            {/* dangerouslySetInnerHTML is deliberate — see CLAUDE.md
                "Trigger H". JSX with text→span→text siblings (prefix→italic
                →suffix) serializes at SSR with React boundary comments
                (`<!-- -->`) between each child. Facebook's in-app browser
                (FB_IAB) strips HTML comments, leaving the CSR DOM without
                them — React 19 strict-compares the byte stream and fires
                #418. Inlining via innerHTML emits a single text node with
                no boundaries for FB IAB to strip. `area` is a Number cast
                from arithmetic, safe to interpolate; the i18n keys are
                author-controlled but escaped defensively. */}
            <h2
              className="mt-3 max-w-md font-display text-[26px] leading-[1.15] font-medium text-ink-700 md:text-[40px]"
              dangerouslySetInnerHTML={{
                __html: `${escapeForHtml(t('SIZE.B2B.TITLE_PREFIX'))} <span class="font-display italic">${area}</span> ${escapeForHtml(t('SIZE.B2B.AREA_UNIT'))}`
              }}
            />
          </div>
          <p className="max-w-md text-[14px] leading-relaxed text-ink-200 md:col-span-7 md:self-end md:text-[15px]">
            {t('SIZE.B2B.SUBTITLE')}
          </p>
        </div>

        <dl className="mt-10 grid grid-cols-1 gap-6 border-t border-ink-100/15 pt-6 md:mt-12 md:grid-cols-3 md:gap-12">
          <div>
            <dt className="font-display text-[11px] tracking-[0.16em] text-ink-200 uppercase md:text-xs">
              {t('SIZE.B2B.DIMENSIONS_LABEL')}
            </dt>
            <dd className="mt-2 font-display text-[22px] leading-none font-medium text-ink-700 md:text-[28px]">
              <span className="italic">{width}</span>
              <span className="mx-1 text-ink-200">×</span>
              <span className="italic">{length}</span>{' '}
              <span className="text-[15px] text-ink-200">m</span>
            </dd>
          </div>
          {heightMeters !== null && heightMeters > 0 && (
            <div>
              <dt className="font-display text-[11px] tracking-[0.16em] text-ink-200 uppercase md:text-xs">
                {t('SIZE.B2B.HEIGHT_LABEL')}
              </dt>
              <dd className="mt-2 font-display text-[22px] leading-none font-medium text-ink-700 md:text-[28px]">
                <span className="italic">{heightMeters}</span>{' '}
                <span className="text-[15px] text-ink-200">m</span>
              </dd>
            </div>
          )}
          <div>
            <dt className="font-display text-[11px] tracking-[0.16em] text-ink-200 uppercase md:text-xs">
              {t('SIZE.B2B.ACCESS_LABEL')}
            </dt>
            <dd className="mt-2 text-[14px] leading-relaxed text-ink-700 md:text-[15px]">
              {t('SIZE.B2B.ACCESS_DEFAULT')}
            </dd>
          </div>
        </dl>
      </section>
    )
  }

  const features = [
    {
      Icon: canStoreVehicle ? DirectionsCarIcon : DoNotDisturbIcon,
      label: canStoreVehicle
        ? 'Sí puedes guardar vehículos'
        : 'No puedes guardar vehículos',
      active: !!canStoreVehicle
    },
    {
      Icon: isIndoor ? HomeIcon : WbSunnyIcon,
      label: indoorOutdoorData?.label ?? 'Interior',
      active: true
    },
    ...(privateSpace
      ? [
          {
            Icon: LockOpenIcon,
            label: 'Alojamiento privado',
            active: true
          }
        ]
      : [])
  ]

  return (
    <>
      <StorageSizeModal
        open={guideOpen}
        handleClose={() => setGuideOpen(false)}
        defaultCategory={categoryId}
        defaultSizeId={sizeId}
        surface="listing_size_section"
      />

      <section className="border-t border-ink-100/15 py-12 md:py-24">
        {/* Eyebrow + heading on a 12-col rail. */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-12">
          <div className="md:col-span-5">
            <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
              Tamaño
            </p>
            {/* dangerouslySetInnerHTML is deliberate — see CLAUDE.md
                "Trigger H". JSX with element→text siblings (italic→suffix)
                serializes at SSR with React boundary comments (`<!-- -->`)
                between them. FB_IAB strips comments → React 19 #418.
                Inlining emits a single continuous payload — no boundaries
                for FB IAB to strip. `area` is a safe numeric. */}
            <h2
              className="mt-3 max-w-md font-display text-[26px] leading-[1.15] font-medium text-ink-700 md:text-[40px]"
              dangerouslySetInnerHTML={{
                __html: `<span class="font-display italic">${area}</span> m² para tus cosas`
              }}
            />
          </div>
          <p className="max-w-md text-[14px] leading-relaxed text-ink-200 md:col-span-7 md:self-end md:text-[15px]">
            Esta es sólo una aproximación del espacio disponible — contacta al
            anfitrión si necesitas medidas exactas o más información.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-10 md:mt-14 md:grid-cols-12 md:gap-12">
          {/* Left rail — dimensions + features */}
          <div className="md:col-span-7">
            <dl className="border-t border-ink-100/15 pt-6">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <dt className="font-display text-[11px] tracking-[0.16em] text-ink-200 uppercase md:text-xs">
                  Dimensiones
                </dt>
                <dd className="font-display text-[28px] leading-none font-medium text-ink-700 md:text-[36px]">
                  <span className="italic">{area}</span>
                  <span className="ml-1 text-[18px] text-ink-200 md:text-xl">
                    m²
                  </span>
                </dd>
                <dd className="text-[13px] text-ink-200 md:text-sm">
                  {/* Single template literal — adjacent text interpolations
                      emit `<!-- -->` boundary comments at SSR that comment-
                      stripping clients remove → #418 (Trigger H, hydration_diff
                      2026-07-25 on /alojamiento/private/*). */}
                  {`${width} m × ${length} m`}
                  {typeof size?.height === 'number' && size.height > 0 ? (
                    <span>{` · ${size.height} m de altura`}</span>
                  ) : null}
                </dd>
              </div>
              <div className="mt-2 inline-flex items-center gap-2">
                <span className="font-display text-[11px] tracking-[0.16em] text-blue-500 uppercase md:text-xs">
                  {label}
                </span>
              </div>
            </dl>

            {/* Features row */}
            <div className="mt-10 border-t border-ink-100/15 pt-6">
              <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
                ¿Funcionará?
              </p>
              <h3 className="mt-3 max-w-md font-display text-[20px] leading-tight font-medium text-ink-700 md:text-[24px]">
                Lo que cabe aquí
              </h3>
              <ul className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
                {features.map((f, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-3 border border-ink-100/25 px-4 py-3"
                  >
                    <f.Icon
                      sx={{ fontSize: 18 }}
                      className={
                        f.active && f.Icon !== DoNotDisturbIcon
                          ? 'text-blue-500'
                          : 'text-ink-200'
                      }
                    />
                    <span className="text-[14px] text-ink-700 md:text-[15px]">
                      {f.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA */}
            <div className="mt-10 flex flex-col items-start gap-2 border-t border-ink-100/15 pt-6 md:flex-row md:items-center md:justify-between md:gap-6">
              <p className="max-w-md text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
                ¿No estás seguro qué tamaño necesitas?
              </p>
              <button
                type="button"
                onClick={() => setGuideOpen(true)}
                className="group inline-flex items-center gap-2 text-[12px] font-medium tracking-[0.14em] text-ink-700 uppercase underline decoration-blue-500/40 underline-offset-[6px] transition-colors hover:text-blue-500 hover:decoration-blue-500"
              >
                Ver guía de tamaños
                <span aria-hidden="true">→</span>
              </button>
            </div>
          </div>

          {/* Right rail — image */}
          {image && (
            <div className="md:col-span-5">
              <div className="relative aspect-[5/4] w-full overflow-hidden border border-ink-100/15 bg-cream-200 md:aspect-square">
                {/* Plain <img>, NOT <Image fill> — next/image's `fill` mode
                    injects a `style="position:absolute;…;color:transparent"`
                    block at SSR that drifts on hydration → #418 (Trigger D,
                    same class as the host-avatar fix). Static reference
                    illustration; `src` LAST to avoid attribute-order drift. */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  alt={`Espacio ${label}`}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 size-full object-contain"
                  src={image}
                />
                <span className="absolute top-4 left-4 font-display text-[11px] tracking-[0.22em] text-ink-200 uppercase md:text-xs">
                  Referencia
                </span>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  )
}

export default React.memo(SizeSection)
